<template>
  <div class="unit-settings">
    <v-card class="settings-card" elevation="0">
      <!-- Header with BACK button -->
      <div class="d-flex justify-end mb-6">
        <v-btn
          class="text-none"
          height="36"
          color="primary"
          outlined
          @click="handleBack"
        >
          <span class="text-caption font-weight-medium">BACK</span>
        </v-btn>
      </div>

      <!-- Main Content -->
      <div v-if="$store.state.loading" class="text-center pa-10">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
        <div class="mt-4">Loading settings...</div>
      </div>
      <div v-else class="settings-content">
        <!-- Section 1: Default Units of Measure -->
        <div class="section mb-6">
          <h2 class="section-title mb-2">Select the default Units of Measure</h2>
          <h3 class="section-subtitle mb-4">Default Unit of Measure for defining Area and Perimeter</h3>
          
          <v-checkbox
            v-model="allowUserOverride"
            label="Allow User to Override"
            class="mb-6"
            hide-details
          ></v-checkbox>

          <!-- Unit Selection Grid -->
          <v-row class="unit-grid">
            <v-col
              v-for="(unit, index) in unitFields"
              :key="index"
              cols="12"
              md="4"
              class="unit-field-col"
            >
              <div class="unit-label">{{ unit.label }}</div>
              <v-select
                v-model="unitValues[unit.key]"
                :items="unit.options"
                item-text="name"
                item-value="id"
                outlined
                dense
                placeholder="Specify from dropdown"
                hide-details
                class="unit-select"
              ></v-select>
            </v-col>
          </v-row>
        </div>

        <!-- Divider -->
        <v-divider class="my-6"></v-divider>

        <!-- Section 2: Additional Units -->
        <div class="section mb-6">
          <v-row class="unit-grid">
            <v-col
              v-for="(unit, index) in additionalUnitFields"
              :key="index"
              cols="12"
              md="4"
              class="unit-field-col"
            >
              <div class="unit-label">{{ unit.label }}</div>
              <v-select
                v-model="unitValues[unit.key]"
                :items="unit.options"
                item-text="name"
                item-value="id"
                outlined
                dense
                placeholder="Specify from dropdown"
                hide-details
                class="unit-select"
              ></v-select>
            </v-col>
          </v-row>
        </div>

        <!-- Divider -->
        <v-divider class="my-6"></v-divider>

        <!-- Section 3: Packing Units -->
        <div class="section">
          <h2 class="section-title mb-4">Packing Units</h2>
          <v-row>
            <v-col cols="12" md="4">
              <div class="unit-label">Bag</div>
              <div class="d-flex align-center">
                <v-text-field
                  v-model.number="packingUnits.bag.quantity"
                  type="number"
                  outlined
                  dense
                  hide-details
                  class="mr-2"
                  style="max-width: 100px;"
                ></v-text-field>
                <v-select
                  v-model="packingUnits.bag.unitId"
                  :items="weightUnits"
                  item-text="name"
                  item-value="id"
                  outlined
                  dense
                  hide-details
                  class="flex-grow-1"
                ></v-select>
              </div>
            </v-col>

            <v-col cols="12" md="4">
              <div class="unit-label">Box</div>
              <div class="d-flex align-center">
                <v-text-field
                  v-model.number="packingUnits.box.quantity"
                  type="number"
                  outlined
                  dense
                  hide-details
                  class="mr-2"
                  style="max-width: 100px;"
                ></v-text-field>
                <v-select
                  v-model="packingUnits.box.unitId"
                  :items="weightUnits"
                  item-text="name"
                  item-value="id"
                  outlined
                  dense
                  hide-details
                  class="flex-grow-1"
                ></v-select>
              </div>
            </v-col>

            <v-col cols="12" md="4">
              <div class="unit-label">Bottle</div>
              <div class="d-flex align-center">
                <v-text-field
                  v-model.number="packingUnits.bottle.quantity"
                  type="number"
                  outlined
                  dense
                  hide-details
                  class="mr-2"
                  style="max-width: 100px;"
                ></v-text-field>
                <v-select
                  v-model="packingUnits.bottle.unitId"
                  :items="volumeUnits"
                  item-text="name"
                  item-value="id"
                  outlined
                  dense
                  hide-details
                  class="flex-grow-1"
                ></v-select>
              </div>
            </v-col>
          </v-row>
        </div>

        <!-- Save Button -->
        <div class="d-flex justify-end mt-6">
          <v-btn
            class="green-button text-none"
            height="40"
            @click="handleSave"
            :loading="saving"
          >
            <span>SAVE</span>
          </v-btn>
        </div>
      </div>
    </v-card>
  </div>
</template>

<script>
import GeneralService from '@/_services/GeneralService';
import PermissionsService from '@/_services/PermissionsService';
import loadingMixin from "@/mixins/LoadingMixin";

export default {
  name: 'UnitSettings',
  mixins: [loadingMixin],
  data() {
    return {
      allowUserOverride: false,
      saving: false,
      unitValues: {},
      unitTypes: [],
      packingUnits: {
        bag: { quantity: 5, unitId: null },
        box: { quantity: 5, unitId: null },
        bottle: { quantity: 5, unitId: null },
      },
      weightUnits: [],
      volumeUnits: [],
      unitFields: [],
      additionalUnitFields: [],
      organizationUnitSettings: {},
      // Unit field mapping - maps UI keys to API unit type names
      unitFieldMapping: {
        weight: 'Weight',
        length: 'Length',
        volumeArea: 'Volume-Area',
        weightArea: 'Weight-Area',
        area: 'Area',
        perimeter: 'Perimeter',
        irrigationArea: 'Irrigation-Area',
        irrigationVolume: 'Irrigation-Volume',
        storageArea: 'Storage-Area',
        storageYield: 'Storage-Yield',
        harvestingFreshYield: 'Harvesting-Fresh-Yield',
        harvestingYieldHouseholdConsumption: 'Harvesting-Yield-Household-Consumption',
        herbicideDoseRate: 'Herbicide-Dose-Rate',
        herbicideUsed: 'Herbicide-Used',
        energyConsumption: 'Energy-Consumption',
        thickness: 'Thickness',
        potassiumUnit: 'Potassium Unit',
        limmingRateWeightUnit: 'Limming Rate Weight Unit',
        totalLimeWeightUnit: 'Total Lime Weight Unit',
        syntheticFertilizerApplicationRateWeightAreaUnit: 'Synthetic Fertilizer Application Rate WeightArea Unit',
        totalSyntheticFertilizerApplicationRateWeightUnit: 'Total Synthetic Fertilizer Application Rate Weight Unit',
        phosphorusUnit: 'Phosphorus Unit',
        bulkDensity: 'Bulk Density',
        nitrogenUnit: 'Nitrogen Unit',
        sulphurUnit: 'Sulphur Unit',
        syntheticFertilizerNitrogenUnit: 'Synthetic Fertilizer Nitrogen Unit',
        syntheticFertilizerPhosphorusUnit: 'Synthetic Fertilizer Phosphorus Unit',
        syntheticFertilizerPotassiumUnit: 'Synthetic Fertilizer Potassium Unit',
        density: 'Density',
        coffeeParchmentDensityUnit: 'Coffee Parchment Density Unit',
        temperature: 'Temperature',
      },
    };
  },
  async mounted() {
    this.startLoading();
    try {
      await this.fetchUnitTypes();
      this.initializeUnitFields();
      await this.fetchOrganizationSettings();
    } catch (error) {
      console.error('Error loading settings:', error);
      this.$notify({
        title: 'Failed to load settings',
        type: 'error',
      });
    } finally {
      this.stopLoading();
    }
  },
  methods: {
    async fetchUnitTypes() {
      try {
        const response = await GeneralService.getAllUnitTypes();
        if (response.success && response.data) {
          this.unitTypes = response.data;
          this.processUnitTypes();
        }
      } catch (error) {
        console.error('Error fetching unit types:', error);
        throw error;
      }
    },
    processUnitTypes() {
      // Extract weight and volume units for packing units
      const weightUnitType = this.unitTypes.find(ut => ut.name === 'Weight');
      const volumeUnitType = this.unitTypes.find(ut => ut.name === 'Volume' || ut.name === 'Volume-Area');
      
      if (weightUnitType && weightUnitType.units) {
        this.weightUnits = weightUnitType.units.map(unit => ({
          id: unit.id,
          name: unit.name,
          abbvr: unit.abbvr,
        }));
      }
      
      if (volumeUnitType && volumeUnitType.units) {
        this.volumeUnits = volumeUnitType.units.map(unit => ({
          id: unit.id,
          name: unit.name,
          abbvr: unit.abbvr,
        }));
      }
    },
    initializeUnitFields() {
      // Create unit fields from unit types
      const mainUnitTypes = [
        'Weight', 'Length', 'Volume-Area', 'Weight-Area', 'Area', 'Perimeter',
        'Irrigation-Area', 'Irrigation-Volume', 'Storage-Area', 'Storage-Yield',
        'Harvesting-Fresh-Yield', 'Harvesting-Yield-Household-Consumption',
        'Herbicide-Dose-Rate', 'Herbicide-Used', 'Energy-Consumption', 'Thickness'
      ];
      
      const additionalUnitTypes = [
        'Potassium Unit', 'Limming Rate Weight Unit', 'Total Lime Weight Unit',
        'Synthetic Fertilizer Application Rate WeightArea Unit',
        'Total Synthetic Fertilizer Application Rate Weight Unit',
        'Phosphorus Unit', 'Bulk Density', 'Nitrogen Unit', 'Sulphur Unit',
        'Synthetic Fertilizer Nitrogen Unit', 'Synthetic Fertilizer Phosphorus Unit',
        'Synthetic Fertilizer Potassium Unit', 'Density', 'Coffee Parchment Density Unit',
        'Temperature'
      ];
      
      this.unitFields = mainUnitTypes
        .map(unitTypeName => {
          const unitType = this.unitTypes.find(ut => ut.name === unitTypeName);
          if (!unitType || !unitType.units) return null;
          
          const key = Object.keys(this.unitFieldMapping).find(
            k => this.unitFieldMapping[k] === unitTypeName
          );
          
          return {
            key: key || unitTypeName.toLowerCase().replace(/\s+/g, ''),
            label: unitType.label || unitType.name,
            unitTypeId: unitType.id,
            options: unitType.units.map(unit => ({
              id: unit.id,
              name: unit.name,
              abbvr: unit.abbvr,
            })),
          };
        })
        .filter(field => field !== null);
      
      this.additionalUnitFields = additionalUnitTypes
        .map(unitTypeName => {
          const unitType = this.unitTypes.find(ut => ut.name === unitTypeName);
          if (!unitType || !unitType.units) return null;
          
          const key = Object.keys(this.unitFieldMapping).find(
            k => this.unitFieldMapping[k] === unitTypeName
          );
          
          return {
            key: key || unitTypeName.toLowerCase().replace(/\s+/g, ''),
            label: unitType.label || unitType.name,
            unitTypeId: unitType.id,
            options: unitType.units.map(unit => ({
              id: unit.id,
              name: unit.name,
              abbvr: unit.abbvr,
            })),
          };
        })
        .filter(field => field !== null);
      
      // Initialize unitValues
      [...this.unitFields, ...this.additionalUnitFields].forEach((field) => {
        if (!this.unitValues[field.key]) {
          this.unitValues[field.key] = null;
        }
      });
    },
    async fetchOrganizationSettings() {
      try {
        const response = await PermissionsService.getGlobalSettings();
        if (response.success && response.data) {
          const { globalSetting, organizationUnits } = response.data;
          
          if (globalSetting) {
            // Load allowUserOverride if available (may need to be added to GlobalSetting model)
            this.allowUserOverride = globalSetting.allowUserOverride || false;
          }
          
          // Load existing organization unit settings
          if (organizationUnits && Object.keys(organizationUnits).length > 0) {
            this.organizationUnitSettings = organizationUnits;
            
            const newUnitValues = { ...this.unitValues };
            
            // Map organization units to unitValues using the field mapping
            Object.keys(this.unitFieldMapping).forEach(key => {
              const unitTypeName = this.unitFieldMapping[key];
              if (organizationUnits[unitTypeName] && organizationUnits[unitTypeName].unitId) {
                newUnitValues[key] = organizationUnits[unitTypeName].unitId;
              }
            });
            
            [...this.unitFields, ...this.additionalUnitFields].forEach(field => {
              const unitType = this.unitTypes.find(ut => ut.id === field.unitTypeId);
              if (unitType && organizationUnits[unitType.name] && organizationUnits[unitType.name].unitId) {
                newUnitValues[field.key] = organizationUnits[unitType.name].unitId;
              }
            });
            
            this.unitValues = newUnitValues;
          }
          
          // Load packing units if available (may need separate endpoint or storage)
          // For now, packing units can be stored separately or in a JSON field
          if (globalSetting && globalSetting.packingUnits) {
            try {
              const packingUnits = typeof globalSetting.packingUnits === 'string' 
                ? JSON.parse(globalSetting.packingUnits) 
                : globalSetting.packingUnits;
              this.packingUnits = { ...this.packingUnits, ...packingUnits };
            } catch (e) {
              console.warn('Could not parse packing units:', e);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching organization settings:', error);
        throw error;
      }
    },
    handleBack() {
      this.$router.go(-1);
    },
    async handleSave() {
      this.saving = true;
      try {
        // Build units array in the format expected by API
        const units = [];
        
        [...this.unitFields, ...this.additionalUnitFields].forEach((field) => {
          const unitId = this.unitValues[field.key];
          if (unitId && field.unitTypeId) {
            units.push({
              unitTypeId: field.unitTypeId,
              unitId: unitId,
            });
          }
        });
        
        const settingsData = {
          allowUserOverride: this.allowUserOverride,
          units: units,
          packingUnits: this.packingUnits,
        };
        
        // Use the global settings API endpoint
        const response = await PermissionsService.updateGlobalSettings(settingsData);
        
        if (response.success) {
          this.$notify({
            title: 'Settings saved successfully',
            type: 'success',
          });
        } else {
          throw new Error(response.message || 'Failed to save settings');
        }
      } catch (error) {
        console.error('Error saving settings:', error);
        this.$notify({
          title: 'Failed to save settings',
          text: error.message || 'An error occurred while saving settings',
          type: 'error',
        });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.unit-settings {
  .settings-card {
    background-color: #ffffff;
    border-radius: 4px;
    padding: 24px;
  }

  .settings-content {
    max-width: 100%;
  }

  .section-title {
    font-size: 18px;
    font-weight: 600;
    color: #1e1e1e;
  }

  .section-subtitle {
    font-size: 14px;
    font-weight: 500;
    color: #787878;
  }

  .unit-label {
    font-size: 14px;
    font-weight: 500;
    color: #1e1e1e;
    margin-bottom: 8px;
  }

  .unit-grid {
    margin-top: 16px;
  }

  .unit-field-col {
    margin-bottom: 16px;
  }

  .green-button {
    background-color: #00BD73 !important;
    color: white !important;
    border: none !important;
    
    &:hover {
      background-color: #00a65a !important;
    }
  }

  ::v-deep .v-select {
    .v-input__control {
      min-height: 40px;
    }
  }

  ::v-deep .v-text-field {
    .v-input__control {
      min-height: 40px;
    }
  }
}
</style>



